import { useMemo, useState } from "react";

import {
  IMeasurement,
  IMeasurementTypeValidation,
} from "../../../@types/admin/AdminMeasurement";
import { useUpdateMeasurement } from "../../../hooks/mutations/admin/measurements";
import { useToast } from "../../../hooks/useToast";
import { errorToastMessage } from "../../../utils/HttpError";
import {
  AdminDateField,
  FieldHint,
  FilterButton,
  FilterGroup,
  FilterInput,
  FilterLabel,
  FormGrid,
  ModalContainer,
  ModalForm,
  ModalFormActions,
  ModalTitle,
  ModalHeader,
  ModalSubtitle,
  WarningNotice,
} from "../shared";
import {
  buildMeasurementBody,
  buildMeasurementFields,
  IMeasurementField,
  toDateTimeInputValue,
  validateMeasurements,
} from "./measurementEdit";

interface IEditMeasurementModalProps {
  measurement: IMeasurement;
  measurementTypes: IMeasurementTypeValidation[];
  onClose: () => void;
}

/**
 * Edit a measurement — Retool's `modalMeasurementDetais` (EN-5158).
 *
 * The ported `validateMeasurements` runs before every save and blocks it; the
 * PUT body comes from the ported `buildMeasurementsBody`. Both live in
 * measurementEdit.ts.
 *
 * Two differences from the export. Retool labelled each input with the raw
 * column key ("bp_systolic") while holding the display names unused in
 * `getMeasurementTypes.data`; the names are used here, with the key as a hint
 * so the column being written stays visible. And Retool showed the validation
 * errors only after a failed save attempt — they update as the operator
 * types, and the Save button is disabled while any stand.
 */
export function EditMeasurementModal({
  measurement,
  measurementTypes,
  onClose,
}: IEditMeasurementModalProps) {
  const { showToast } = useToast();
  const { updateMeasurement, isUpdatingMeasurement } = useUpdateMeasurement();

  const [time, setTime] = useState(toDateTimeInputValue(measurement.time));
  const [fields, setFields] = useState<IMeasurementField[]>(() =>
    buildMeasurementFields(measurement, measurementTypes)
  );

  const errors = useMemo(
    () => validateMeasurements(time, fields, measurementTypes),
    [time, fields, measurementTypes]
  );

  const setFieldValue = (key: string, value: string) =>
    setFields((previous) =>
      previous.map((field) => (field.key === key ? { ...field, value } : field))
    );

  const handleSave = async () => {
    if (errors.length > 0) return;

    try {
      await updateMeasurement({
        measurementId: measurement.id,
        request: buildMeasurementBody(time, fields, measurement.time),
      });
      showToast("The measurement was updated.", "success");
      onClose();
    } catch (error) {
      showToast(
        errorToastMessage(
          error,
          "There was an error updating this measurement."
        ),
        "warning"
      );
    }
  };

  return (
    <ModalContainer>
      <ModalHeader>
        <ModalTitle>Edit measurement</ModalTitle>
        <ModalSubtitle>
          Measurement {measurement.id} · {measurement.patientName} · patient ID{" "}
          {measurement.patientId}
        </ModalSubtitle>
      </ModalHeader>

      <ModalForm>
        <FilterGroup>
          <FilterLabel htmlFor="measurement-time">Time</FilterLabel>
          <AdminDateField
            id="measurement-time"
            withTime
            value={time}
            onChange={setTime}
          />
        </FilterGroup>

        <FormGrid>
          {fields.map((field) => (
            <FilterGroup key={field.key}>
              <FilterLabel htmlFor={`measurement-${field.key}`}>
                {field.label}
              </FilterLabel>
              <FilterInput
                id={`measurement-${field.key}`}
                type="text"
                inputMode="decimal"
                value={field.value}
                onChange={(e) => setFieldValue(field.key, e.target.value)}
              />
              <FieldHint>{field.key}</FieldHint>
            </FilterGroup>
          ))}
        </FormGrid>

        {errors.length > 0 && (
          <WarningNotice>
            {errors.map((error) => (
              <span key={error}>
                {error}
                <br />
              </span>
            ))}
          </WarningNotice>
        )}

        <FieldHint>
          A field left blank clears that value on the measurement.
        </FieldHint>

        <ModalFormActions>
          <FilterButton
            variant="secondary"
            disabled={isUpdatingMeasurement}
            onClick={onClose}
          >
            Cancel
          </FilterButton>
          <FilterButton
            variant="primary"
            disabled={isUpdatingMeasurement || errors.length > 0}
            onClick={handleSave}
          >
            {isUpdatingMeasurement ? "Saving..." : "Save"}
          </FilterButton>
        </ModalFormActions>
      </ModalForm>
    </ModalContainer>
  );
}
